import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /**
   * Guard async submenu height updates from stale hover activations.
   * @type {number}
   */
  #activeActivationId = 0;

  /**
   * @type {ReturnType<typeof setTimeout> | null}
   */
  #submenuObserverCleanupTimer = null;

  /**
   * Delay closing so moving between items (or diagonally into the panel) does not collapse the menu.
   * @type {ReturnType<typeof setTimeout> | null}
   */
  #closeTimer = null;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.addEventListener('pointerleave', this.#onMenuPointerLeave);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.removeEventListener('pointerleave', this.#onMenuPointerLeave);
    this.#cancelClose();
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#cleanupMutationObserver();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  #overflowSubmenuListener = () => {
    this.#deactivateImmediately();
  };

  /**
   * @param {PointerEvent} event
   */
  #onMenuPointerLeave = (event) => {
    if (this.#isInsideMenu(event.relatedTarget)) return;

    this.#scheduleClose();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Whether the pointer is still inside this menu (including overflow shadow DOM).
   * @param {EventTarget | null} target
   * @returns {boolean}
   */
  #isInsideMenu(target) {
    if (!(target instanceof Node)) return false;
    if (this.contains(target)) return true;

    return Boolean(this.refs.overflowMenu?.shadowRoot?.contains(target));
  }

  #cancelClose() {
    if (!this.#closeTimer) return;

    clearTimeout(this.#closeTimer);
    this.#closeTimer = null;
  }

  /**
   * @param {HTMLElement | null} [item]
   */
  #scheduleClose(item = this.#state.activeItem) {
    this.#cancelClose();
    this.#closeTimer = setTimeout(() => {
      this.#closeTimer = null;
      this.#deactivateImmediately(item);
    }, 120);
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Close any open submenu panel (e.g. when search opens on desktop).
   */
  closeActiveSubmenu() {
    this.#deactivateImmediately();
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#cancelClose();
    // pointerover fires when moving between descendants; skip those so we only
    // activate on entering the list item (event.target is the li via on: delegation).
    if (event.type === 'pointerover' && isMovingWithinTarget(event)) return;

    if (window.matchMedia('(min-width: 750px)').matches) {
      /** @type {HTMLElement & { closeDialog?: () => void; refs?: { dialog?: HTMLDialogElement } } | null} */
      const searchModal = document.getElementById('search-modal');
      if (searchModal?.refs?.dialog?.open) {
        searchModal.closeDialog?.();
      }
    }

    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    const item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
      const previousSubmenu = findSubmenu(previouslyActiveItem);
      if (previousSubmenu) delete previousSubmenu.dataset.active;
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);
    const activationId = ++this.#activeActivationId;

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      submenu.dataset.active = '';
      this.#cleanupMutationObserver();

      this.#submenuMutationObserver = new MutationObserver(() => {
        this.#updateSubmenuHeight(item, submenu, isDefaultSlot, hasSubmenu, activationId);

        if (submenu.offsetHeight > 0) this.#cleanupMutationObserver();
      });
      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

      this.#submenuObserverCleanupTimer = setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    this.#updateSubmenuHeight(item, submenu, isDefaultSlot, hasSubmenu, activationId);
  };

  /**
   * Deactivate the active item after a short delay, unless the pointer stayed in the menu.
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (event.type === 'pointerout' && isMovingWithinTarget(event)) return;

    if (!(event.target instanceof Element)) return;

    const relatedTarget = 'relatedTarget' in event ? event.relatedTarget : null;

    if (this.#isInsideMenu(relatedTarget)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingToSubmenu =
      relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(relatedTarget);
    const isMovingToOverflowMenu =
      relatedTarget instanceof Node && relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingToSubmenu || isMovingToOverflowMenu) return;

    this.#scheduleClose();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivateImmediately = (item = this.#state.activeItem) => {
    this.#cancelClose();
    if (!item || item != this.#state.activeItem) return;

    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';
    this.#activeActivationId += 1;

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    if (this.#submenuObserverCleanupTimer) {
      clearTimeout(this.#submenuObserverCleanupTimer);
      this.#submenuObserverCleanupTimer = null;
    }

    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }

  /**
   * Measure and apply submenu height for the currently active activation only.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} submenu
   * @param {boolean} isDefaultSlot
   * @param {boolean} hasSubmenu
   * @param {number} activationId
   */
  #updateSubmenuHeight(item, submenu, isDefaultSlot, hasSubmenu, activationId) {
    if (activationId !== this.#activeActivationId) return;
    if (this.#state.activeItem !== item) return;
    if (!this.headerComponent) return;

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Ignore pointerover/out that stay inside the element that owns the handler.
 * @param {Event} event
 */
function isMovingWithinTarget(event) {
  return (
    event.target instanceof Node && event.relatedTarget instanceof Node && event.target.contains(event.relatedTarget)
  );
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element.matches('[slot="more"]')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  const menuitem = element.querySelector('[ref="menuitem"]');
  return menuitem instanceof HTMLElement ? menuitem : null;
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
